/**
 * Dependencies:
 * - Custom select component
 *
 * Required translation strings:
 * - addToCart
 * - noStock
 * - noVariant
 * - onlyXLeft
 */

if (!customElements.get('variant-picker')) {
  class VariantPicker extends HTMLElement {
    constructor() {
      super();
      this.section = this.closest('.js-product');
      this.productForm = this.section.querySelector('.js-product-form');
      this.optionSelectors = this.querySelectorAll('.option-selector:not(.option-selector--custom)');
      this.nativeSelector = document.getElementById(`variants-${this.section.dataset.section}`);
      this.data = this.getVariantData();
      this.selectedOptions = this.getSelectedOptions();
      this.variant = this.getSelectedVariant();

      this.updateAvailability();
      this.updateAddToCartButton();
      this.updateQuantityInputMax();
      this.addEventListener('change', this.handleVariantChange.bind(this));
      this.applySearchParams();
      this.initPriceGroupingSwatches();
      // Capitalize initial color text values
      this.capitalizeInitialColorText();
      this.setupListedOptionKeyboardSupport();
      this.syncListedOptionKeyboardState();

      this.setAttribute('loaded', '');
    }

    /**
     * Handles 'change' events on the variant picker element.
     * @param {object} evt - Event object.
     */
    handleVariantChange(evt) {
      // If selection contains a URL for different product
      const productUrl = evt.target.dataset.productUrl || evt.detail?.productUrl;
      if (productUrl) {
        // Check for product change
        const selectedUrl = new URL(productUrl, window.location.origin);
        if (selectedUrl.pathname !== window.location.pathname) {
          // Preserve options
          const params = `option_values=${this.getSelectedOptionIds().join(',')}`;
          const separator = productUrl.includes('?') ? '&' : '?';
          window.location = `${productUrl}${separator}${params}`;
          return;
        }
      }

      // Determine which option group changed
      const changedSelector = evt.target.closest('.option-selector');
      const changedIndex = changedSelector ? Array.from(this.optionSelectors).indexOf(changedSelector) : -1;

      this.selectedOptions = this.getSelectedOptions();
      this.variant = this.getSelectedVariant();

      // If the exact selection does not map to a variant, resolve to the closest in-stock variant
      if ((!this.variant && changedIndex > -1) || (!this.variant.available)) {
        const resolved = this.resolveClosestAvailableVariant(changedIndex);
        if (resolved) {
          // Adjust other options to match a valid, in-stock combination
          resolved.options.forEach((val, idx) => {
            const selector = this.optionSelectors[idx];
            if (!selector) return;
            const current = this.selectedOptions[idx];
            if (current !== val) {
              this.selectOptionByValue(selector, val);
            }
          });

          // Refresh internal state after programmatic changes
          this.selectedOptions = this.getSelectedOptions();
          this.variant = this.getSelectedVariant();
        }
      }

      this.preSelection = !this.variant && this.selectedOptions.find((o) => o === null) === null;

      this.updateUrl(evt);
      this.updateVariantInput();
      this.updateAddToCartButton();
      this.updateAvailability();
      this.updatePrice();
      this.updateBackorderText();
      this.updatePickupAvailability();
      this.updateSku();
      this.updateBarcode();
      this.updateQuantityInputMax();
      VariantPicker.updateLabelText(evt, this);
      this.syncListedOptionKeyboardState();

      this.dispatchEvent(new CustomEvent('on:variant:change', {
        bubbles: true,
        detail: {
          form: this.productForm,
          variant: this.variant,
          allVariants: this.data.variants,
          selectedOptions: this.selectedOptions
        }
      }));
    }

    setupListedOptionKeyboardSupport() {
      this.querySelectorAll('.option-selector[data-selector-type="listed"] .opt-label[for]').forEach((label) => {
        if (!label.hasAttribute('tabindex')) label.setAttribute('tabindex', '0');
        if (!label.hasAttribute('role')) label.setAttribute('role', 'button');

        if (!label.dataset.keyboardBound) {
          label.addEventListener('keydown', (evt) => {
            if (evt.key !== 'Enter' && evt.key !== ' ') return;
            evt.preventDefault();
            label.click();
          });
          label.dataset.keyboardBound = 'true';
        }
      });
    }

    syncListedOptionKeyboardState() {
      this.querySelectorAll('.option-selector[data-selector-type="listed"] .opt-label[for]').forEach((label) => {
        const targetId = label.getAttribute('for');
        if (!targetId) return;
        const escapedTargetId = (window.CSS && CSS.escape) ? CSS.escape(targetId) : targetId.replace(/"/g, '\\"');
        const input = this.querySelector(`#${escapedTargetId}`);
        if (!input) return;
        label.setAttribute('aria-pressed', input.checked ? 'true' : 'false');
      });
    }

    /**
     * Resolve the closest in-stock variant after a change.
     * Prioritizes keeping the changed option fixed and maximizing
     * matches with the other already-selected values.
     * @param {number} changedIndex
     * @returns {?object}
     */
    resolveClosestAvailableVariant(changedIndex) {
      const inStock = this.data.variants.filter((v) => v.available);
      if (inStock.length === 0) return null;

      // 1) Try to find an exact in-stock match respecting current selections
      const exact = inStock.find((v) => this.selectedOptions.every((val, i) => val === null || v.options[i] === val));
      if (exact) return exact;

      // 2) Keep the changed option fixed, then pick variant with max matches
      const candidates = inStock.filter((v) => v.options[changedIndex] === this.selectedOptions[changedIndex]);
      if (candidates.length === 0) return null;

      let best = null;
      let bestScore = -1;
      candidates.forEach((v) => {
        let score = 0;
        this.selectedOptions.forEach((val, i) => {
          if (i === changedIndex) return;
          if (val !== null && v.options[i] === val) score += 1;
        });
        if (score > bestScore) {
          best = v;
          bestScore = score;
        }
      });
      return best;
    }

    /**
     * Select a given value on an option selector (radio grid or dropdown)
     * without user interaction.
     * @param {Element} selector
     * @param {string} value
     */
    selectOptionByValue(selector, value) {
      const esc = (window.CSS && CSS.escape) ? CSS.escape(value) : value.replace(/"/g, '\\"');
      if (selector.dataset.selectorType === 'dropdown') {
        const optionEl = selector.querySelector(`.custom-select__option[data-value="${esc}"]`);
        if (optionEl) {
          const customSelect = optionEl.closest('custom-select');
          if (customSelect && typeof customSelect.selectOption === 'function') {
            customSelect.selectOption(optionEl);
          } else if (optionEl.offsetParent !== null && !optionEl.disabled) {
            optionEl.click();
          }
        }
      } else {
        const input = selector.querySelector(`input[value="${esc}"]`);
        if (input && !input.checked) {
          input.checked = true;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
    }

    /**
     * Updates the "Add to Cart" button label and status.
     */
    updateAddToCartButton() {
      if (!this.productForm) return;
      if (this.selectedOptions.indexOf(null) > -1) return;

      this.addBtn = this.addBtn || this.productForm.querySelector('[name="add"]');
      const variantAvailable = this.variant && this.variant.available;
      const unavailableStr = this.variant ? theme.strings.noStock : theme.strings.noVariant;

      this.addBtn.disabled = !variantAvailable;
      this.addBtn.textContent = variantAvailable
        ? this.addBtn.dataset.addToCartText
        : unavailableStr;
    }

    /**
     * Updates the availability status of an option.
     * @param {Element} optionEl - Option element.
     * @param {boolean} exists - Does this option lead to a variant that exists?
     * @param {boolean} available - Does this option lead to a variant that is available to buy?
     */
    static updateOptionAvailability(optionEl, exists, available) {
      const el = optionEl;
      const unavailableText = exists ? theme.strings.noStock : theme.strings.noVariant;
      el.classList.toggle('is-unavailable', !available);
      el.classList.toggle('is-nonexistent', !exists);

      if (optionEl.classList.contains('custom-select__option')) {
        const em = el.querySelector('em');

        if (em) {
          em.hidden = available;
        }

        if (!available) {
          if (em) {
            em.textContent = unavailableText;
          } else {
            el.innerHTML = `${el.innerHTML} <em class="pointer-events-none">${unavailableText}</em>`;
          }
        }
      } else if (!available) {
        el.nextElementSibling.title = unavailableText;
      } else {
        el.nextElementSibling.removeAttribute('title');
      }
    }

    /**
     * Updates the availability status in option selectors.
     */
    updateAvailability() {
      if (this.dataset.availability === 'off') return;

      this.optionSelectors.forEach((selector, selectorIndex) => {
        const options = selector.querySelectorAll('.js-option:not([data-value=""])');
        let optionThatExists = false;
        options.forEach((option) => {
          // Skip combined listings
          if (option.dataset.productUrl) return;

          const optionValue = selector.dataset.selectorType === 'dropdown' ? option.dataset.value : option.value;
          // any available variants with previous options and this one locked in?
          let variantsExist = false;
          let variantsAvailable = false;
          this.data.variants.forEach((v) => {
            let matches = 0;
            for (let i = 0; i < selectorIndex; i += 1) {
              if (v.options[i] === this.selectedOptions[i] || this.selectedOptions[i] === null) {
                matches += 1;
              }
            }
            if (v.options[selectorIndex] === optionValue && matches === selectorIndex) {
              variantsExist = true;
              if (!optionThatExists) {
                optionThatExists = option;
              }
              if (v.available) {
                variantsAvailable = true;
              }
            }
          });
          VariantPicker.updateOptionAvailability(option, variantsExist, variantsAvailable);
        });
        if (this.dataset.availability === 'prune' && optionThatExists && selector.querySelector('.js-option.is-nonexistent:is(:checked, [aria-selected="true"])')) {
          setTimeout(() => {
            if (optionThatExists.closest('custom-select')) {
              optionThatExists.closest('custom-select').selectOption(optionThatExists);
            } else {
              optionThatExists.click();
            }
          }, 0);
        }
      });
    }

    /**
     * Capitalizes variant title while preserving size abbreviations.
     * @param {string} title - The variant title to capitalize.
     * @returns {string} - The capitalized variant title.
     */
    capitalizeVariantTitle(title) {
      if (!title) return '';

      const parts = title.split('/');
      const resultParts = [];

      for (let i = 0; i < parts.length; i++) {
        const part = parts[i].trim();
        const partUpper = part.toUpperCase();

        // Check if this part looks like a size (typically 1-3 characters, all uppercase or numbers)
        const isSize = /^[A-Z0-9]{1,3}$/.test(partUpper) ||
                      /^[0-9]+[X]$/.test(partUpper) ||  // For sizes like 1X, 2X, etc.
                      /^[A-Z]+[0-9]*$/.test(partUpper); // For sizes like PS, PM, etc.

        if (isSize) {
          resultParts.push(partUpper);
        } else {
          // Capitalize other parts
          const words = part.split(' ');
          const capitalizedWords = words.map(word => {
            if (word.length === 0) return word;
            return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
          });
          resultParts.push(capitalizedWords.join(' '));
        }
      }

      return resultParts.join(' / ');
    }

    updateBackorderText() {
      this.backorder = this.backorder || this.section.querySelector('.backorder');
      if (!this.backorder) return;

      let hideBackorder = true;

      if (this.variant && this.variant.available) {
        const { inventory } = this.data.formatted[this.variant.id];

        if (this.variant.inventory_management && inventory === 'none') {
          const backorderProdEl = this.backorder.querySelector('.backorder__product');
          const prodTitleEl = this.section.querySelector('.product-title');
          const variantTitle = this.variant.title.includes('Default') ? '' : ` - ${this.capitalizeVariantTitle(this.variant.title)}`;

          backorderProdEl.textContent = `${prodTitleEl.textContent}${variantTitle}`;
          hideBackorder = false;
        }
      }

      this.backorder.hidden = hideBackorder;
    }

    /**
     * Capitalizes initial color text values on page load.
     */
    capitalizeInitialColorText() {
      // Convert "METALIC GEM" -> "Metalic Gem" for PDP selected color text
      this.querySelectorAll('.js-color-text').forEach(el => {
        if (el.textContent.trim()) {
          el.textContent = el.textContent.toLowerCase().replace(/\b\w/g, char => char.toUpperCase());
        }
      });
    }

    /**
     * Updates the colour option label text.
     * @param {object} evt - Event object
     * @param {VariantPicker} variantPicker - The variant picker instance
     */
    static updateLabelText(evt, variantPicker) {
      // Safety checks
      if (!variantPicker || !variantPicker.optionSelectors) return;

      // Find the color selector (the one with .js-color-text)
      const colorSelector = Array.from(variantPicker.optionSelectors).find(selector => {
        return selector && selector.querySelector('.js-color-text') !== null;
      });

      if (!colorSelector) return;
      if (colorSelector.dataset.selectorType === 'dropdown') return;

      const colorText = colorSelector.querySelector('.js-color-text');
      if (!colorText) return;

      // Get the color option index
      const colorSelectorIndex = Array.from(variantPicker.optionSelectors).indexOf(colorSelector);

      // Safety check: ensure index is valid
      if (colorSelectorIndex === -1) return;

      // Get the color value from the variant's options array
      let colorValue = '';
      if (variantPicker.variant &&
          variantPicker.variant.options &&
          variantPicker.variant.options[colorSelectorIndex] !== undefined) {
        colorValue = variantPicker.variant.options[colorSelectorIndex];
      } else {
        // Fallback: get from the selected input in the color selector
        const selectedInput = colorSelector.querySelector('input:checked');
        if (selectedInput) {
          const valueElement = selectedInput.nextElementSibling?.querySelector('.js-value');
          if (valueElement) {
            colorValue = valueElement.textContent;
          }
        }
      }

      if (colorValue) {
        // Apply capitalization: "METALIC GEM" -> "Metalic Gem"
        colorText.textContent = colorValue.toLowerCase().replace(/\b\w/g, char => char.toUpperCase());
      }
    }

    /**
     * Updates the pick up availability.
     */
    updatePickupAvailability() {
      this.pickUpAvailability = this.pickUpAvailability || this.section.querySelector('pickup-availability');
      if (!this.pickUpAvailability) return;

      if (this.variant && this.variant.available) {
        this.pickUpAvailability.getAvailability(this.variant.id);
      } else {
        this.pickUpAvailability.removeAttribute('available');
        this.pickUpAvailability.innerHTML = '';
      }
    }

    /**
     * Updates the price.
     */
    updatePrice() {
      this.price = this.price || this.section.querySelector('.product-info__price .price');
      if (!this.price) return;

      let { variant } = this;
      if (this.preSelection) {
        variant = this.data.variants[0];
        for (let i = 1; i < this.data.variants.length; i += 1) {
          if (this.data.variants[i].price < variant.price) variant = this.data.variants[i];
        }
      }

      if (variant) {
        const priceCurrentEl = this.price.querySelector('.price__current');
        const priceWasEl = this.price.querySelector('.price__was');
        const unitPriceEl = this.price.querySelector('.unit-price');

        // Update price.
        if (variant.compare_at_price > variant.price) {
          priceCurrentEl.querySelector('.js-label').textContent = theme.strings.salePrice;
          priceCurrentEl.querySelector('.js-value').innerHTML = this.data.formatted[variant.id].price;
          priceWasEl.querySelector('.js-label').textContent = theme.strings.regularPrice;
          priceWasEl.querySelector('.js-value').innerHTML = this.data.formatted[variant.id].compareAtPrice || '';
        } else {
          priceCurrentEl.querySelector('.js-label').textContent = theme.strings.regularPrice;
          priceCurrentEl.querySelector('.js-value').innerHTML = this.data.formatted[variant.id].price;
          priceWasEl.querySelector('.js-label').textContent = '';
          priceWasEl.querySelector('.js-value').innerHTML = '';
        }

        // Update unit price, if specified.
        if (variant.unit_price_measurement) {
          const valueEl = this.price.querySelector('.unit-price__price');
          const unitEl = this.price.querySelector('.unit-price__unit');
          const value = variant.unit_price_measurement.reference_value;
          const unit = variant.unit_price_measurement.reference_unit;

          valueEl.innerHTML = this.data.formatted[variant.id].unitPrice;
          unitEl.textContent = value === 1 ? unit : `${value} ${unit}`;
          unitPriceEl.hidden = false;
        } else if (unitPriceEl) {
          unitPriceEl.hidden = true;
        }

        this.price.classList.toggle('price--on-sale', variant.compare_at_price > variant.price);
        this.price.classList.toggle('price--sold-out', !variant.available && !this.preSelection);
      }

      this.price.querySelector('.price__default').hidden = !this.variant && !this.preSelection;
      this.price.querySelector('.price__no-variant').hidden = this.variant || this.preSelection;
      const from = this.price.querySelector('.price__from');
      if (from) {
        from.hidden = !this.preSelection;
      }
    }

    /**
     * Updates the SKU.
     */
    updateSku() {
      this.sku = this.sku || this.section.querySelector('.product-sku__value');
      if (!this.sku) return;

      const skuAvailable = this.variant && this.variant.sku;
      this.sku.textContent = skuAvailable ? this.variant.sku : '';
      this.sku.parentNode.hidden = !skuAvailable;
    }

    /**
     * Updates the Barcode.
     */
    updateBarcode() {
      this.barcode = this.barcode || this.section.querySelector('.product-barcode__value');
      if (!this.barcode) return;

      const barcodeAvailable = this.variant && this.variant.barcode;
      this.barcode.textContent = barcodeAvailable ? this.variant.barcode : '';
      this.barcode.parentNode.hidden = !barcodeAvailable;
    }

    /**
     * Updates the quantity input max attribute based on variant inventory.
     * Targets quantity-wrapper > input[name="quantity"] and always replaces the max attribute.
     * Works for both regular product pages and quickbuy modals.
     */
    updateQuantityInputMax() {
      if (!this.productForm) return;

      // Find quantity-wrapper first, then get the input inside it
      const quantityWrapper = this.productForm.querySelector('quantity-wrapper');
      const quantityInput = quantityWrapper
        ? quantityWrapper.querySelector('input[name="quantity"]')
        : this.productForm.querySelector('input[name="quantity"]');

      if (!quantityInput) return;

      // If variant exists, check for inventory management
      // Inventory data is now included in variant object via getVariantData()
      if (this.variant) {
        // Get inventory data from variant object (supports both camelCase and snake_case)
        const hasInventoryManagement = !!(this.variant.inventory_management || this.variant.inventoryManagement);
        const inventoryQuantity = this.variant.inventoryQuantity ??
                                 this.variant.inventory_quantity ??
                                 null;

        // Only proceed if we have inventory management enabled
        if (!hasInventoryManagement) {
          // No inventory management - remove max restriction
          quantityInput.removeAttribute('max');
          quantityInput.removeAttribute('disabled');
          return;
        }

        // Use 0 if inventoryQuantity is null/undefined
        const finalInventoryQuantity = inventoryQuantity !== null ? inventoryQuantity : 0;

        // Always replace the max attribute based on current variant inventory
        if (finalInventoryQuantity > 0) {
          quantityInput.setAttribute('max', String(finalInventoryQuantity));
          quantityInput.removeAttribute('disabled');

          // If current value exceeds max, adjust it to max
          const currentValue = parseInt(quantityInput.value, 10) || 1;
          if (currentValue > finalInventoryQuantity) {
            quantityInput.value = String(finalInventoryQuantity);
            // Trigger change event to notify quantity-wrapper
            quantityInput.dispatchEvent(new CustomEvent('change', { bubbles: true, cancelable: false }));
          }
        } else {
          // Out of stock - set max to 0 and disable input
          quantityInput.setAttribute('max', '0');
          quantityInput.disabled = true;
          quantityInput.value = '1';
        }
      } else {
        // No variant selected - remove max restriction until variant is selected
        quantityInput.removeAttribute('max');
        quantityInput.removeAttribute('disabled');
      }

      // Trigger input event to notify quantity-wrapper component
      quantityInput.dispatchEvent(new Event('input', { bubbles: true, cancelable: false }));
    }

    /**
     * Updates the url with the selected variant id.
     * @param {object} evt - Event object.
     */
    updateUrl(evt) {
      if (!evt || evt.type !== 'change' || this.dataset.updateUrl === 'false') return;
      const url = this.variant ? `${this.dataset.url}?variant=${this.variant.id}` : this.dataset.url;
      window.history.replaceState({ }, '', url);
    }

    /**
     * Updates the value of the hidden [name="id"] form inputs.
     */
    updateVariantInput() {
      this.forms = this.forms || this.section.querySelectorAll('.js-product-form, .js-instalments-form');

      this.forms.forEach((form) => {
        const input = form.querySelector('[name="id"]');
        input.value = this.variant ? this.variant.id : '';
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }

    /**
     * Gets the variant data for a product.
     * @returns {?object}
     */
    getVariantData() {
      const dataEl = this.querySelector('[type="application/json"]:not(.variant-inventory-data)');
      const inventoryDataEl = this.querySelector('.variant-inventory-data');
      const data = JSON.parse(dataEl.textContent);

      // Enhance variant objects with inventory data
      if (inventoryDataEl && data.variants) {
        try {
          const inventoryData = JSON.parse(inventoryDataEl.textContent);
          data.variants.forEach((variant) => {
            if (inventoryData[variant.id]) {
              // Add inventory data to variant object (both snake_case and camelCase)
              variant.inventory_quantity = inventoryData[variant.id].inventory_quantity;
              variant.inventory_management = inventoryData[variant.id].inventory_management;
              variant.inventoryQuantity = inventoryData[variant.id].inventory_quantity;
              variant.inventoryManagement = inventoryData[variant.id].inventory_management;
            }
          });
        } catch (e) {
          // If inventory data parsing fails, continue without it
        }
      }

      return data;
    }

    /**
     * Get the selected values from a list of variant options.
     * @returns {Array} Array of selected option values, value is null if not selected.
     */
    getSelectedOptions() {
      const selectedOptions = [];

      this.optionSelectors.forEach((selector) => {
        if (selector.dataset.selectorType === 'dropdown') {
          const selected = selector.querySelector('.custom-select__option[aria-selected="true"]');
          selectedOptions.push(selected && selected.dataset.value !== '' ? selected.dataset.value : null);
        } else {
          const selected = selector.querySelector('input:checked');
          selectedOptions.push(selected ? selected.value : null);
        }
      });

      return selectedOptions;
    }

    /**
     * Get the selected option value ids from a list of variant options.
     * @returns {Array} Array of selected option value ids, value is null if not selected.
     */
    getSelectedOptionIds() {
      const selectedOptionIds = [];

      this.optionSelectors.forEach((selector) => {
        if (selector.dataset.selectorType === 'dropdown') {
          const selected = selector.querySelector('.custom-select__option[aria-selected="true"]');
          selectedOptionIds.push(selected && selected.dataset.value !== '' ? selected.dataset.valueId : null);
        } else {
          const selected = selector.querySelector('input:checked');
          selectedOptionIds.push(selected ? selected.dataset.valueId : null);
        }
      });

      return selectedOptionIds;
    }

    /**
     * Get selected variant data.
     * @returns {?object} Variant object, or null if one is not selected.
     */
    getSelectedVariant() {
      return this.data.variants.find(
        (v) => v.options.every((val, index) => val === this.selectedOptions[index])
      );
    }

    /**
     * Apply search parameters.
     */
    applySearchParams() {
      const searchParams = new URLSearchParams(window.location.search);

      Array.from(searchParams.keys()).forEach((key) => {
        const value = searchParams.get(key);
        const optionSelectors = Array.from(this.optionSelectors);
        const matchingOptionSelector = optionSelectors.find((x) => x.dataset.option === key);
        if (!matchingOptionSelector) return;

        if (matchingOptionSelector.dataset.selectorType === 'dropdown') {
          const colorOptionDropdown = matchingOptionSelector.querySelector(`.custom-select__option[data-value="${value}"]`);
          if (colorOptionDropdown) {
            const customSelect = colorOptionDropdown.closest('custom-select');
            customSelect.selectOption(colorOptionDropdown);
          }
        } else {
          const matchingInput = matchingOptionSelector.querySelector(`input[value="${value}"]`);
          if (matchingInput) {
            const matchingOptionValue = matchingOptionSelector.querySelector('.option-selector__label-value.js-color-text');
            if (matchingOptionValue) {
              // Capitalize the color value: "METALIC GEM" -> "Metalic Gem"
              matchingOptionValue.textContent = value.toLowerCase().replace(/\b\w/g, char => char.toUpperCase());
            }
            matchingInput.checked = true;
            matchingInput.dispatchEvent(
              new CustomEvent('change', { bubbles: true, cancelable: false })
            );
          }
        }
      });
    }

    /**
      * Initialize price grouping swatch synchronization (optimized & accessible)
      */
    initPriceGroupingSwatches() {
      // Cache selectors for performance
      const priceGroupings = this.querySelectorAll('.variant-price-grouping');
      if (priceGroupings.length === 0) return;

      priceGroupings.forEach((priceGrouping) => {
        const optionSelector = priceGrouping.closest('.option-selector');
        if (!optionSelector) return;

        // Cache commonly used elements
        const labelValue = optionSelector.querySelector('.option-selector__label-value.js-color-text');
        const mainSelector = optionSelector.querySelector('.option-selector__btns:not(.variant-price-grouping .option-selector__btns)');

        if (!labelValue) return;

        // Use event delegation for better performance
        priceGrouping.addEventListener('change', (event) => {
          const target = event.target;
          if (target.type !== 'radio' || !target.checked) return;

          this.handlePriceGroupSwatchChange(target, priceGrouping, labelValue, mainSelector);
        });

        // Handle main selector changes if it exists
        if (mainSelector) {
          mainSelector.addEventListener('change', (event) => {
            const target = event.target;
            if (target.type !== 'radio' || !target.checked) return;

            this.handleMainSwatchChange(target, priceGrouping, labelValue);
          });
        }
      });
    }

    /**
     * Handle price group swatch change with accessibility
     * @param {HTMLElement} target - The changed input
     * @param {HTMLElement} priceGrouping - The price grouping container
     * @param {HTMLElement} labelValue - The label value element
     * @param {HTMLElement} mainSelector - The main selector element
     */
    handlePriceGroupSwatchChange(target, priceGrouping, labelValue, mainSelector) {
      if (!target || !labelValue) return;

      const colorName = target.dataset.colorName || target.value;
      if (!colorName) return;

      // Update label value with capitalization: "METALIC GEM" -> "Metalic Gem"
      labelValue.textContent = colorName.toLowerCase().replace(/\b\w/g, char => char.toUpperCase());

      // Sync selection across all groups (uncheck others)
      const allSwatches = priceGrouping.querySelectorAll('input[type="radio"]');
      allSwatches.forEach(input => {
        if (input !== target) input.checked = false;
      });

      // Sync with main variant selector
      if (mainSelector) {
        const mainInput = mainSelector.querySelector(`input[value="${target.value}"]`);
        if (mainInput && !mainInput.checked) {
          mainInput.checked = true;
          mainInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }

      // Announce change to screen readers
      this.announceSwatchChange(colorName, target);
    }

    /**
     * Handle main swatch change
     * @param {HTMLElement} target - The changed input
     * @param {HTMLElement} priceGrouping - The price grouping container
     * @param {HTMLElement} labelValue - The label value element
     */
    handleMainSwatchChange(target, priceGrouping, labelValue) {
      if (!target || !labelValue) return;

      const colorName = target.dataset.colorName || target.value;
      if (!colorName) return;

      // Update label value with capitalization: "METALIC GEM" -> "Metalic Gem"
      labelValue.textContent = colorName.toLowerCase().replace(/\b\w/g, char => char.toUpperCase());

      // Sync price grouping swatches
      const correspondingSwatch = priceGrouping.querySelector(`input[value="${target.value}"]`);
      const allSwatches = priceGrouping.querySelectorAll('input[type="radio"]');

      allSwatches.forEach(input => {
        input.checked = (input === correspondingSwatch);
      });
    }

    /**
     * Announce swatch change to screen readers
     * @param {string} colorName - The color name
     * @param {HTMLElement} input - The input element
     */
    announceSwatchChange(colorName, input) {
      // Create or update screen reader announcement
      let announcer = document.getElementById('swatch-announcer');
      if (!announcer) {
        announcer = document.createElement('div');
        announcer.id = 'swatch-announcer';
        announcer.setAttribute('aria-live', 'polite');
        announcer.setAttribute('aria-atomic', 'true');
        announcer.className = 'visually-hidden';
        document.body.appendChild(announcer);
      }

      // Get price information from aria-label
      const priceInfo = input.getAttribute('aria-label') || '';
      announcer.textContent = `Selected ${colorName}. ${priceInfo}`;
    }
  }

  customElements.define('variant-picker', VariantPicker);
}
